import { Component, Input } from '@angular/core';
import { TranslateModule } from '@client/app/modules/translate.module';
import { JiraConnectorRepo } from '@client/app/feature-modules/shared/jira-connector/services/jira-connector.repo';
import { NgbModalOpenerService } from '@client/app/services/ngb-modal-opener/ngb-modal-opener.service';
import { SessionService } from '@monsido/core/session/session.service';
import { CreateJiraIssueModalComponent } from '@client/app/components/jira/create-issue-modal.component';
import { NgbTooltipMfeModule } from '@client/mfe/overlay/ngb-tooltip-mfe.module';

interface JiraIssueError {
    jira_issue_url: string | null;
    error_page_id: number;
}

type JiraCheck = unknown;

type JiraPage = unknown;

@Component({
    selector: 'jira-issue-buttons',
    standalone: true,
    imports: [NgbTooltipMfeModule, TranslateModule],
    template: `
        @if (hasJiraConnector) {
            @if (error?.jira_issue_url === null) {
                <button class="btn btn-border-radius btn-secondary mr-1"
                        triggers="focus hover"
                        ngbTooltip="{{ 'Create issue in Jira' | translate }}"
                        container="body"
                        (click)="createJiraIssue(error)"
                        [disabled]="isCreatingJiraIssue">
                    @if (!isCreatingJiraIssue) {
                        <span class="fab fa-jira" aria-hidden="true"></span>
                    }
                    @if (isCreatingJiraIssue) {
                        <span class="fas fa-spinner fa-spin" role="status" aria-hidden="true"></span>
                    }
                    <span class="ml-1">{{ 'Create issue' | translate }}</span>
                </button>
            }

            @if (error?.jira_issue_url) {
                <button class="btn btn-border-radius btn-secondary mr-1"
                        triggers="focus hover"
                        ngbTooltip="{{ 'View issue in Jira' | translate }}"
                        container="body"
                        (click)="viewJiraIssue(error)">
                    <span class="fab fa-jira" aria-hidden="true"></span>
                    <span class="ml-1">{{ 'View issue' | translate }}</span>
                </button>
            }
        }
    `,
})
export class JiraIssueButtonsComponent {
    @Input() error: JiraIssueError | null = null;
    @Input() check: JiraCheck | null = null;
    @Input() page: JiraPage | null = null;
    @Input() hasJiraConnector: boolean = false;

    isCreatingJiraIssue: boolean = false;

    constructor (
        private jiraConnectorRepo: JiraConnectorRepo,
        private ngbModalOpenerService: NgbModalOpenerService,
        private sessionService: SessionService,
    ) {}

    createJiraIssue (error: JiraIssueError | null): void {
        if (!error) {
            return;
        }
        const modal = this.ngbModalOpenerService.open({
            component: CreateJiraIssueModalComponent,
            size: 'sm',
            resolve: {
                error: error,
                check: this.check,
                page: this.page,
                user: this.sessionService.me,
            },
        }) as { componentInstance: CreateJiraIssueModalComponent; close: () => void; dismiss: () => void };

        modal.componentInstance.close.subscribe((payload: { title: string; description: string }) => {
            this.isCreatingJiraIssue = true;
            this.jiraConnectorRepo.createIssue({
                issue_id: error.error_page_id,
                issue_type: 'accessibility',
                title: payload.title,
                description: payload.description,
            }).then(
                (response: { url: string }) => {
                    error.jira_issue_url = response.url;
                    this.isCreatingJiraIssue = false;
                    modal.close();
                },
                () => {
                    this.isCreatingJiraIssue = false;
                },
            );
        });

        modal.componentInstance.dismiss.subscribe(() => {
            modal.dismiss();
        });
    }

    viewJiraIssue (error: JiraIssueError | null): void {
        if (error?.jira_issue_url) {
            window.open(error.jira_issue_url, '_blank');
        }
    }
}
